import React, { useCallback, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import Dropzone from 'react-dropzone';
import Feather from 'react-native-vector-icons/Feather';
import { MB_Button } from '@/components/general/button';
import { MB_Modal } from '@/components/general/modal';
import { isMobileApp } from '@/utils/general/device-info';
import { mbTextStyles } from '@/utils/general/style-utils';
import { COLORS } from '../../../constants/colors';
import { textStyles } from '../../../constants/textStyles';
import { RESOURCE_POOL_TYPE } from '../../../typesAndInterfaces/typesAndInterfaces';
import { BulkUploadItemRow } from './BulkUploadItemRow';
import { pickFilesOnMobile, processPickedFiles } from './bulkUploadFileProcessing';
import { BulkUploadFile, BulkUploadItem, BULK_UPLOAD_ITEM_STATUS, BULK_UPLOAD_MAX_FILES } from './bulkUploadTypes';
import { useBulkUploadQueue } from './useBulkUploadQueue';

interface BulkUploadModalProps {
  isVisible: boolean;
  onDismiss: () => void;
  resourcePoolType: RESOURCE_POOL_TYPE;
  folderId?: string;
}

/**
 * Bulk upload flow: pick many files, review each one (name, tags, duplicate warning,
 * remove), then upload through the existing single-upload endpoints with limited
 * concurrency, per-file progress and per-file retry.
 */
const BulkUploadModal = ({ isVisible, onDismiss, resourcePoolType, folderId }: BulkUploadModalProps) => {
  const isImagePool = resourcePoolType === RESOURCE_POOL_TYPE.Image;
  const poolTypeLabel = isImagePool ? 'image' : 'video';

  const { items, addItems, updateItem, removeItem, reset, startUpload, retryItem, isUploading, hasUploadRun } = useBulkUploadQueue({ resourcePoolType, folderId });
  const [isProcessingSelection, setIsProcessingSelection] = useState(false);
  const [selectionNotices, setSelectionNotices] = useState<string[]>([]);

  const remainingSlots = BULK_UPLOAD_MAX_FILES - items.length;

  const onFilesPicked = useCallback(
    async (files: BulkUploadFile[]) => {
      if (files.length === 0) {
        return;
      }
      const notices: string[] = [];
      let filesToProcess = files;
      if (files.length > remainingSlots) {
        filesToProcess = files.slice(0, remainingSlots);
        notices.push(`You can upload at most ${BULK_UPLOAD_MAX_FILES} files per batch. ${files.length - remainingSlots} file(s) were not added.`);
      }
      setIsProcessingSelection(true);
      try {
        const processed = await processPickedFiles({ files: filesToProcess, resourcePoolType });
        addItems(processed.items);
        setSelectionNotices([...notices, ...processed.notices]);
      } finally {
        setIsProcessingSelection(false);
      }
    },
    [addItems, remainingSlots, resourcePoolType]
  );

  const onDropWeb = useCallback(
    (acceptedFiles: File[], fileRejections: { file: File }[]) => {
      const notices = fileRejections.map(rejection => `${rejection.file.name}: unsupported file type, skipped.`);
      if (notices.length > 0) {
        setSelectionNotices(notices);
      }
      onFilesPicked(acceptedFiles.map(file => ({ blob: file, uri: URL.createObjectURL(file), fileName: file.name })));
    },
    [onFilesPicked]
  );

  const onPickOnMobile = useCallback(async () => {
    const files = await pickFilesOnMobile({ resourcePoolType, selectionLimit: remainingSlots });
    onFilesPicked(files);
  }, [onFilesPicked, remainingSlots, resourcePoolType]);

  // Names that collide inside the batch itself (server-side duplicates are per-row).
  const duplicateNamesInBatch = useMemo(() => {
    const nameCounts = new Map<string, number>();
    for (const item of items) {
      const key = item.name.trim().toLowerCase();
      if (key) {
        nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
      }
    }
    return new Set([...nameCounts.entries()].filter(([, count]) => count > 1).map(([key]) => key));
  }, [items]);

  const pendingItems = items.filter(item => item.status === BULK_UPLOAD_ITEM_STATUS.ready || item.status === BULK_UPLOAD_ITEM_STATUS.error);
  const successCount = items.filter(item => item.status === BULK_UPLOAD_ITEM_STATUS.success).length;
  const errorCount = items.filter(item => item.status === BULK_UPLOAD_ITEM_STATUS.error).length;
  const overallProgress = items.length === 0 ? 0 : Math.round(items.reduce((total, item) => total + (item.status === BULK_UPLOAD_ITEM_STATUS.success ? 100 : item.progress), 0) / items.length);

  const onUploadAllPress = useCallback(() => {
    // Mirror single-upload validation: name and at least one tag are required.
    // Intra-batch duplicate names are blocked too — the backend has no unique
    // constraint, so both would otherwise be created.
    let hasInvalidItems = false;
    for (const item of pendingItems) {
      const patch: Partial<BulkUploadItem> = {};
      if (!item.name.trim() || duplicateNamesInBatch.has(item.name.trim().toLowerCase())) {
        patch.showNameError = true;
        hasInvalidItems = true;
      }
      if (item.tags.length === 0) {
        patch.showTagsError = true;
        hasInvalidItems = true;
      }
      if (Object.keys(patch).length > 0) {
        updateItem(item.id, patch);
      }
    }
    if (!hasInvalidItems) {
      startUpload();
    }
  }, [duplicateNamesInBatch, pendingItems, startUpload, updateItem]);

  const onModalDismiss = useCallback(() => {
    if (isUploading || isProcessingSelection) {
      return;
    }
    reset();
    setSelectionNotices([]);
    onDismiss();
  }, [isProcessingSelection, isUploading, onDismiss, reset]);

  const dropAreaContent = (
    <View style={styles.dropArea}>
      <Feather name="upload-cloud" color={COLORS.textLightPurple} size={28} />
      <Text style={styles.dropAreaText}>
        {isMobileApp ? `Tap to select up to ${remainingSlots} ${poolTypeLabel}s` : `Drag & drop ${poolTypeLabel}s here, or click to browse (up to ${remainingSlots} more)`}
      </Text>
    </View>
  );

  return (
    <MB_Modal isVisible={isVisible} onDismiss={onModalDismiss} title={`Bulk Upload ${isImagePool ? 'Images' : 'Videos'}`} childrenWrapperStyle={styles.modal}>
      <View style={styles.modalBody}>
      {remainingSlots > 0 &&
        !isUploading &&
        (isMobileApp ? (
          <View style={styles.dropAreaWrapper}>
            <MB_Button title="" style={styles.mobilePickButton} onPress={onPickOnMobile}>
              {dropAreaContent}
            </MB_Button>
          </View>
        ) : (
          <View style={styles.dropAreaWrapper}>
            <Dropzone onDrop={onDropWeb} multiple accept={isImagePool ? BULK_UPLOAD_ACCEPTED_IMAGE_TYPES : BULK_UPLOAD_ACCEPTED_VIDEO_TYPES}>
              {({ getRootProps, getInputProps }) => (
                <section style={{ width: '100%' }}>
                  <div {...getRootProps()} style={webDropzoneStyle} data-testid="bulk-upload-dropzone">
                    {dropAreaContent}
                  </div>
                  <input {...getInputProps()} />
                </section>
              )}
            </Dropzone>
          </View>
        ))}

      {isProcessingSelection && <Text style={styles.noticeText}>Processing selected files…</Text>}
      {selectionNotices.map((notice, index) => (
        <Text key={index} style={styles.noticeText}>
          {notice}
        </Text>
      ))}

      {items.length > 0 && (
        <ScrollView style={styles.list}>
          {items.map(item => (
            <BulkUploadItemRow
              key={item.id}
              item={item}
              resourcePoolType={resourcePoolType}
              isDuplicateInBatch={duplicateNamesInBatch.has(item.name.trim().toLowerCase())}
              isUploading={isUploading}
              onItemChange={updateItem}
              onRemove={removeItem}
              onRetry={retryItem}
            />
          ))}
        </ScrollView>
      )}

      {items.length > 0 && (
        <View style={styles.footer}>
          {(isUploading || hasUploadRun) && (
            <View style={styles.overallProgressWrapper}>
              <Text style={styles.overallProgressText}>
                {isUploading
                  ? `Uploading… ${successCount}/${items.length} done (${overallProgress}%)`
                  : `Done: ${successCount} uploaded${errorCount > 0 ? `, ${errorCount} failed` : ''}`}
              </Text>
              <View style={styles.overallProgressTrack}>
                <View style={[styles.overallProgressFill, { width: `${overallProgress}%` }]} />
              </View>
            </View>
          )}
          <View style={styles.footerButtons}>
            {errorCount > 0 && !isUploading && (
              <MB_Button title={`Retry ${errorCount} Failed`} style={styles.retryAllButton} textStyle={{ color: COLORS.buttonPurple }} onPress={() => startUpload()} />
            )}
            {pendingItems.length > 0 && (
              <MB_Button
                title={isUploading ? 'Uploading…' : `Upload ${pendingItems.length} ${isImagePool ? 'Image' : 'Video'}${pendingItems.length > 1 ? 's' : ''}`}
                disabled={isUploading || isProcessingSelection}
                style={styles.uploadButton}
                onPress={onUploadAllPress}
              />
            )}
            {pendingItems.length === 0 && !isUploading && <MB_Button title="Close" style={styles.uploadButton} onPress={onModalDismiss} />}
          </View>
        </View>
      )}
      </View>
    </MB_Modal>
  );
};

export { BulkUploadModal };

// Same extension lists the single-upload picker allows (MB_ResourcePickerGetAllowedFileFormats),
// expressed in the Accept-object shape react-dropzone v14 expects.
const BULK_UPLOAD_ACCEPTED_IMAGE_TYPES = { 'image/*': ['.jpeg', '.jpg', '.png', '.jfif', '.webp'] };
const BULK_UPLOAD_ACCEPTED_VIDEO_TYPES = { 'video/*': ['.avi', '.mp4', '.mov', '.wmv', '.flv', '.mkv', '.webm'] };

const webDropzoneStyle: any = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  cursor: 'pointer',
};

const styles = StyleSheet.create({
  modal: {
    width: 900,
    maxWidth: '95%',
    maxHeight: '90%',
  },
  modalBody: {
    width: '100%',
  },
  dropAreaWrapper: {
    width: '100%',
    marginBottom: 12,
  },
  mobilePickButton: {
    backgroundColor: 'transparent',
    height: undefined,
  },
  dropArea: {
    width: '100%',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: COLORS.buttonPurple,
    borderRadius: 10,
    alignItems: 'center',
    paddingVertical: 24,
  },
  dropAreaText: mbTextStyles([
    textStyles.smallText,
    {
      color: COLORS.textLightPurple,
      marginTop: 8,
    },
  ]),
  noticeText: mbTextStyles([
    textStyles.smallText,
    {
      color: '#e6a245',
      marginBottom: 4,
      textAlign: 'left',
    },
  ]),
  list: {
    width: '100%',
    maxHeight: 400,
    marginTop: 8,
  },
  footer: {
    width: '100%',
    marginTop: 12,
  },
  overallProgressWrapper: {
    width: '100%',
    marginBottom: 12,
  },
  overallProgressText: mbTextStyles([
    textStyles.smallText,
    {
      textAlign: 'left',
      marginBottom: 4,
    },
  ]),
  overallProgressTrack: {
    height: 8,
    borderRadius: 4,
    backgroundColor: '#3d3654',
    overflow: 'hidden',
  },
  overallProgressFill: {
    height: '100%',
    borderRadius: 4,
    backgroundColor: COLORS.buttonPurple,
  },
  footerButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    columnGap: 12,
  },
  retryAllButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: COLORS.buttonPurple,
    height: 38,
    borderRadius: 10,
    paddingHorizontal: 16,
  },
  uploadButton: {
    backgroundColor: COLORS.buttonPurple,
    height: 38,
    borderRadius: 10,
    paddingHorizontal: 16,
  },
});
